<script setup>
import { useHead, useHeadRaw } from '@vueuse/head'
import { computed, onServerPrefetch } from 'vue'
import { useStore } from 'vuex'

import useWork from '@mixins/Work'
import { LayoutContentContainer as ContentContainer } from '@web-nfb/vue-3-components'
import NotFound from '@pages/NotFound'
import Loading from '@components/Loading'
import { durationToString } from '@helpers/duration'
import UnderlinedTitle from '@components/UnderlinedTitle'
import Info from '@components/Detail/Info'
import OppositeWorkList from '@components/Detail/OppositeWorkList'
import Subjects from '@components/Detail/Subjects'
import FullCredits from '@components/Detail/FullCredits'
import Awards from '@components/Detail/Awards'

const store = useStore()

const workType = 'interactive'
const jsonType = 'CreativeWork'

const shouldUseServerPrefetch = computed(() => store.getters['Global/shouldUseServerPrefetch'])

const genreToString = computed(() => {
  return item.value.interactive_category
})

const durationString = computed(() => {
  return `~ ${durationToString(duration.value)}`
})

const overrideFunctions = {
  genreToString,
  durationString
}

const {
  metaTitle,
  metaLinks,
  jsonLdToString,
  item,
  loading,
  meta,
  duration,
  legendList,
  loadByTypeAndSlugOrId
} = useWork(workType, jsonType, overrideFunctions)

useHead({
  title: metaTitle,
  link: metaLinks
})

useHeadRaw({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: jsonLdToString
    }
  ]
})

const infoProps = computed(() => {
  if (!item.value) {
    return {}
  }

  return {
    title: item.value.title,
    legendList: legendList.value,
    shortCredits: {
      directors: item.value.directors,
      producers: item.value.producers
    },
    description: item.value.description,
    mediaList: item.value.thumbnail ? [item.value.thumbnail] : [],
    mediaPattern: meta.value.media.thumbnail_url,
    ratings: item.value.ratings,
    productionModel: item.value.production_model,
    agencyNote: item.value.agency_note
  }
})

onServerPrefetch(async () => {
  if (shouldUseServerPrefetch.value) {
    await loadByTypeAndSlugOrId()
    await item.value
  }
})

</script>
<template>
  <div
    class="interactive-item"
    v-if="item || loading"
  >
    <ContentContainer>
      <template v-if="loading">
        <Loading />
      </template>
      <template v-else>
        <UnderlinedTitle
          class="align-with-logo"
          :title="$t('home.title')"
          tag-title="p"
          tag-class="h4"
        />
        <Info
          v-if="item"
          v-bind="infoProps"
          :type="workType"
        />
        <OppositeWorkList
          v-if="item.opposite_work_list && item.opposite_work_list.length > 0"
          :opposite-work-list="item.opposite_work_list"
        />
        <Subjects
          v-if="item.subjects && item.subjects.length"
          :subjects="item.subjects"
        />
        <FullCredits
          v-if="item.credits && item.credits.length"
          :credits="item.credits"
        />
        <Awards
          v-if="item.awards && item.awards.length"
          :awards="item.awards"
        />
      </template>
    </ContentContainer>
  </div>
  <NotFound
    v-else
    code="404"
  />
</template>
<style lang="less" scoped>
@import (reference) "~@less/shared/utilities.less";
.interactive-item {
  background: @white;
}
</style>
